import { DateTime } from 'luxon';

import { MenuOrientation, ThemeMode } from 'config';

export interface UserDatePreferences {
  dateFormat: string;
  timeFormat: string;
  timezone: string;
  isSideMenuMinimised: boolean;
  menuOrientation: MenuOrientation;
  themeMode: ThemeMode.LIGHT | ThemeMode.DARK;
}

export interface TimezoneOption {
  value: string;
  label: string;
}

/** Fallback when no site is selected or its settings haven't loaded — matches the server default. */
export const DEFAULT_DATE_FORMAT = 'dd/MM/yyyy';

/** Matches the server default, GeneralConstants.DefaultTimeFormat. */
export const DEFAULT_TIME_FORMAT = 'HH:mm';

/** Shown in place of a date that isn't set, so a column of dates keeps its alignment. */
export const DATE_PLACEHOLDER = '—';

export const DEFAULT_USER_DATE_PREFERENCES: UserDatePreferences = {
  dateFormat: DEFAULT_DATE_FORMAT,
  timeFormat: DEFAULT_TIME_FORMAT,
  timezone: 'Pacific/Auckland',
  isSideMenuMinimised: false,
  menuOrientation: MenuOrientation.VERTICAL,
  themeMode: ThemeMode.LIGHT
};

/**
 * Formats a timezone with a fixed standard-time GMT offset. The offset intentionally does not
 * change between daylight-saving seasons, so an option's label remains stable.
 */
export function formatTimezoneLabel(timezone: string): string {
  return formatTimezoneLabelForOffset(timezone, standardTimezoneOffset(timezone));
}

function formatTimezoneLabelForOffset(timezone: string, offset: number): string {
  const sign = offset < 0 ? '-' : '+';
  const absoluteOffset = Math.abs(offset);
  const hours = Math.floor(absoluteOffset / 60);
  const minutes = absoluteOffset % 60;
  return `(GMT${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}) ${timezone}`;
}

function standardTimezoneOffset(timezone: string): number {
  const samples = Array.from({ length: 12 }, (_, index) =>
    DateTime.fromObject({ year: 2026, month: index + 1, day: 1 }, { zone: timezone })
  );
  const standard = samples.find((sample) => /standard/i.test(sample.offsetNameLong ?? '')) ?? samples[0];
  return standard.offset;
}

/**
 * Full IANA list supplied by the browser, with UTC retained for environments that omit it.
 * A valid saved preference is also retained so opening Account Settings never discards it merely
 * because a browser's supported-values implementation differs.
 */
export function getIanaTimezoneOptions(currentTimezone?: string | null): TimezoneOption[] {
  const supported = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const current = currentTimezone?.trim();
  const values = new Set([...supported, 'UTC']);
  if (current && DateTime.now().setZone(current).isValid) values.add(current);

  return [...values]
    .map((timezone) => ({ timezone, offset: standardTimezoneOffset(timezone) }))
    .sort((left, right) => left.offset - right.offset || left.timezone.localeCompare(right.timezone))
    .map(({ timezone, offset }) => ({ value: timezone, label: formatTimezoneLabelForOffset(timezone, offset) }));
}

export function normalizeUserDatePreferences(preferences: Partial<UserDatePreferences> | null | undefined): UserDatePreferences {
  const timezone = preferences?.timezone?.trim();
  const dateFormat = preferences?.dateFormat?.trim();
  const timeFormat = preferences?.timeFormat?.trim();
  return {
    dateFormat: dateFormat || DEFAULT_USER_DATE_PREFERENCES.dateFormat,
    timeFormat: timeFormat || DEFAULT_USER_DATE_PREFERENCES.timeFormat,
    timezone: timezone && DateTime.now().setZone(timezone).isValid ? timezone : DEFAULT_USER_DATE_PREFERENCES.timezone,
    isSideMenuMinimised: preferences?.isSideMenuMinimised === true,
    menuOrientation: preferences?.menuOrientation === MenuOrientation.HORIZONTAL ? MenuOrientation.HORIZONTAL : MenuOrientation.VERTICAL,
    themeMode: preferences?.themeMode === ThemeMode.DARK ? ThemeMode.DARK : ThemeMode.LIGHT
  };
}

/**
 * Reads a value the API returned for a UTC timestamp as a Luxon instant.
 *
 * A .NET DateTime read from SQL has an unspecified Kind, so it serialises without a trailing "Z"
 * even though the stored instant is UTC — hence the explicit `zone: 'utc'` default. `setZone` keeps
 * an offset the string does carry rather than overriding it.
 */
function toUtcInstant(value: string | Date): DateTime {
  return value instanceof Date
    ? DateTime.fromJSDate(value, { zone: 'utc' })
    : DateTime.fromISO(value, { zone: 'utc', setZone: true }).toUTC();
}

/** Formats a UTC instant in the user's configured timezone with their .NET-style date and time patterns. */
export function formatUserDateTime(
  value: string | Date | null | undefined,
  preferences: Pick<UserDatePreferences, 'dateFormat' | 'timezone'> & Partial<Pick<UserDatePreferences, 'timeFormat'>>
): string {
  if (!value) return DATE_PLACEHOLDER;

  const local = toUtcInstant(value).setZone(preferences.timezone);
  if (!local.isValid) return DATE_PLACEHOLDER;

  return `${local.toFormat(toLuxonDatePattern(preferences.dateFormat))} ${local.toFormat(toLuxonTimePattern(preferences.timeFormat))}`;
}

/** Formats a UTC instant as a wall-clock time in the given timezone with a .NET-style time pattern (default HH:mm). */
export function formatUserTime(value: string | Date | null | undefined, timezone: string, timeFormat?: string): string {
  if (!value) return DATE_PLACEHOLDER;

  const local = toUtcInstant(value).setZone(timezone);
  return local.isValid ? local.toFormat(toLuxonTimePattern(timeFormat)) : DATE_PLACEHOLDER;
}

/**
 * Seeds an `<input type="datetime-local">` with the wall-clock time an instant falls on in
 * `timezone`.
 *
 * The box carries no zone of its own, so the pairing with {@link fromDateTimeLocalInputValue} is
 * what says which zone its digits were meant in. Both take the user's configured timezone rather
 * than the browser's, so a docket weighed at 08:15 reads 08:15 wherever it is opened — the same
 * answer the activity summary gives for that visit.
 */
export function toDateTimeLocalInputValue(value: string | Date | null | undefined, timezone: string): string {
  if (!value) return '';

  const local = toUtcInstant(value).setZone(timezone);
  return local.isValid ? local.toFormat("yyyy-MM-dd'T'HH:mm") : '';
}

/** Reads that box back, treating what was keyed as wall-clock time in `timezone`, as a UTC ISO instant. */
export function fromDateTimeLocalInputValue(value: string, timezone: string): string | null {
  if (!value) return null;

  const local = DateTime.fromISO(value, { zone: timezone });
  return local.isValid ? local.toUTC().toISO() : null;
}

/** Formats a UTC instant as a calendar date in the user's configured timezone and date pattern. */
export function formatUserDate(
  value: string | Date | null | undefined,
  preferences: Pick<UserDatePreferences, 'dateFormat' | 'timezone'>
): string {
  if (!value) return DATE_PLACEHOLDER;

  const local = toUtcInstant(value).setZone(preferences.timezone);
  if (!local.isValid) return DATE_PLACEHOLDER;

  return local.toFormat(toLuxonDatePattern(preferences.dateFormat));
}

/** Returns the ISO calendar date that is today in the specified user timezone. */
export function todayInTimezone(timezone: string): string {
  return DateTime.now().setZone(timezone).toISODate() ?? '';
}

/** Converts the start of a user-selected local date into a UTC ISO instant. */
export function localDateStartToUtc(date: string, timezone: string): string | null {
  const local = DateTime.fromISO(date, { zone: timezone }).startOf('day');
  return local.isValid ? local.toUTC().toISO() : null;
}

/** Converts the start of the day after a user-selected local date into a UTC ISO instant. */
export function localDateEndExclusiveToUtc(date: string, timezone: string): string | null {
  const local = DateTime.fromISO(date, { zone: timezone }).plus({ days: 1 }).startOf('day');
  return local.isValid ? local.toUTC().toISO() : null;
}

/**
 * Client-side date formatting utility.
 *
 * Formats date strings or Date objects into a human-readable format.
 * Default output: "28 Mar 2026"
 */

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Formats a date value as "28 Mar 2026".
 *
 * @param value  ISO date string (e.g. "2026-03-28"), Date object, or null/undefined.
 * @returns      Formatted string, or an empty string if the value is falsy/invalid.
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '';

  const d = parseUtcInstant(value);
  if (isNaN(d.getTime())) return '';

  const day = d.getUTCDate();
  const month = MONTH_ABBR[d.getUTCMonth()];
  const year = d.getUTCFullYear();

  return `${day} ${month} ${year}`;
}

/**
 * Formats a date value as "29/03/2024" (DD/MM/YYYY).
 */
export function formatDateDMY(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = parseUtcInstant(value);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Formats a date value as "28 Mar 2026 · 14:35" (date + local time).
 *
 * @param value  ISO date-time string or Date object.
 * @returns      Formatted string, or an empty string if the value is falsy/invalid.
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '';

  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';

  const day = d.getDate();
  const month = MONTH_ABBR[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');

  return `${day} ${month} ${year} · ${hh}:${mm}`;
}

// ─── Site-configured formatting ───────────────────────────────────────────────

const MONTH_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Formats a date against a .NET-style pattern, which is how the date format is stored in site
 * settings (e.g. "dd/MM/yyyy").
 *
 * Parsed and read in UTC, via {@link parseUtcInstant}. These are calendar dates — a lot's start date
 * is the same day everywhere — so both halves have to agree on the zone.
 *
 * Parsing with a bare `new Date()` instead would not: a .NET DateTime from SQL serialises as
 * "2026-08-01T00:00:00", with a time but no zone, and `new Date()` reads that form as *local*.
 * Reading the result back in UTC then shifts the date a day earlier for anyone east of UTC — a lot
 * starting 1 August displayed as 31 July in New Zealand.
 *
 * Supported tokens: yyyy yy MMMM MMM MM M dddd ddd dd d. Anything else in the pattern is treated as
 * a literal, so separators and spacing come through untouched.
 */
export function formatDateWithPattern(value: string | Date | null | undefined, pattern: string = DEFAULT_DATE_FORMAT): string {
  if (!value) return DATE_PLACEHOLDER;

  const d = parseUtcInstant(value);
  if (isNaN(d.getTime())) return DATE_PLACEHOLDER;

  return applyDatePattern(d, pattern, true);
}

/** Substitutes the date tokens of a .NET-style pattern, reading the instant in UTC or locally. */
function applyDatePattern(d: Date, pattern: string, utc: boolean): string {
  const day = utc ? d.getUTCDate() : d.getDate();
  const month = utc ? d.getUTCMonth() : d.getMonth();
  const year = utc ? d.getUTCFullYear() : d.getFullYear();
  const weekday = utc ? d.getUTCDay() : d.getDay();

  const replacements: Record<string, string> = {
    yyyy: String(year).padStart(4, '0'),
    yy: String(year % 100).padStart(2, '0'),
    MMMM: MONTH_FULL[month],
    MMM: MONTH_ABBR[month],
    MM: String(month + 1).padStart(2, '0'),
    M: String(month + 1),
    dddd: DAY_FULL[weekday],
    ddd: DAY_ABBR[weekday],
    dd: String(day).padStart(2, '0'),
    d: String(day)
  };

  // One pass, longest tokens first, so substituted output is never re-scanned — otherwise the 'd' in
  // a substituted "Wed" would itself be replaced.
  return pattern.replace(/yyyy|yy|MMMM|MMM|MM|M|dddd|ddd|dd|d/g, (token) => replacements[token] ?? token);
}

/**
 * Parses a value the API returned for a UTC timestamp.
 *
 * A .NET DateTime read from SQL has an unspecified Kind, so it serialises without a trailing "Z"
 * even though the stored instant is UTC. `new Date()` reads such a string as local time, which
 * silently shifts every timestamp by the viewer's offset — so the zone is pinned to UTC here when
 * the string doesn't carry one.
 *
 * Exported for callers that need the instant itself rather than a formatted string — plotting it on
 * a chart, say.
 */
export function parseUtcInstant(value: string | Date): Date {
  if (value instanceof Date) return value;

  const hasZone = /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(value.trim());
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
  return new Date(hasZone || dateOnly ? value : `${value}Z`);
}

/**
 * How long ago an instant was, as a short elapsed span: "42m", "3h 10m", "2d 4h". Minutes are the
 * smallest unit, so a span reads the same on both apps — this matches the weighbridge's own
 * formatDuration, and the two show the same vehicle the same time onsite.
 *
 * Returns '' for a missing or unparseable value, so a caller can fall back to its own placeholder.
 */
export function formatDuration(from: string | Date | null | undefined, to: string | Date = new Date()): string {
  if (!from) return '';

  const start = parseUtcInstant(from);
  const end = parseUtcInstant(to);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';

  const totalMinutes = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Formats a UTC timestamp as the site's date pattern followed by the time, both in the viewer's
 * local zone (e.g. "28/03/2026 14:35", or "28/03/2026 2:35 PM" on a 12-hour clock).
 *
 * Unlike {@link formatDateWithPattern}, the date is read locally too: these are instants rather than
 * calendar dates, and reading the date in UTC while showing a local clock time would disagree with
 * itself either side of midnight.
 *
 * @param showSeconds Include seconds in the time.
 * @param hour12      Render a 12-hour clock with an AM/PM suffix instead of a 24-hour one.
 */
export function formatDateTimeWithPattern(
  value: string | Date | null | undefined,
  pattern: string = DEFAULT_DATE_FORMAT,
  { showSeconds = false, hour12 = false }: { showSeconds?: boolean; hour12?: boolean } = {}
): string {
  if (!value) return DATE_PLACEHOLDER;

  const d = parseUtcInstant(value);
  if (isNaN(d.getTime())) return DATE_PLACEHOLDER;

  return `${applyDatePattern(d, pattern, false)} ${formatClockTime(d, showSeconds, hour12)}`;
}

/**
 * The local clock time of an instant, on a 24- or 12-hour clock.
 *
 * Minutes and seconds are always padded; the 12-hour hour is not, matching how every platform
 * renders a 12-hour clock ("2:35 PM", not "02:35 PM"). Midnight and noon map to 12, since hour 0
 * has no 12-hour representation.
 */
function formatClockTime(d: Date, showSeconds: boolean, hour12: boolean): string {
  const hours = d.getHours();
  const displayHour = hour12 ? hours % 12 || 12 : hours;

  const parts = [
    hour12 ? String(displayHour) : String(displayHour).padStart(2, '0'),
    String(d.getMinutes()).padStart(2, '0'),
    ...(showSeconds ? [String(d.getSeconds()).padStart(2, '0')] : [])
  ];

  return hour12 ? `${parts.join(':')} ${hours < 12 ? 'AM' : 'PM'}` : parts.join(':');
}

function toLuxonDatePattern(pattern: string): string {
  const tokens: Record<string, string> = {
    yyyy: 'yyyy',
    yy: 'yy',
    MMMM: 'LLLL',
    MMM: 'LLL',
    MM: 'MM',
    M: 'M',
    dddd: 'cccc',
    ddd: 'ccc',
    dd: 'dd',
    d: 'd'
  };

  return pattern.replace(/yyyy|yy|MMMM|MMM|MM|M|dddd|ddd|dd|d/g, (token) => tokens[token] ?? token);
}

/** Converts a .NET-style time pattern (HH:mm, h:mm tt) to Luxon's; only the AM/PM token differs. */
function toLuxonTimePattern(pattern: string = DEFAULT_TIME_FORMAT): string {
  return pattern.replace(/tt/g, 'a');
}

/**
 * Reads a date out of an import file and normalises it to the ISO shape the API can bind to a
 * DateTime — `yyyy-MM-dd` or `yyyy-MM-ddTHH:mm:ss`.
 *
 * Deliberately does **not** use `new Date(...)` to decide whether the value is a date. `new Date`
 * reads a slash-separated value US-first, so `1/08/2026` parses happily as 8 January — an
 * NZ-formatted file would import silently wrong dates. It also accepts values that
 * `System.Text.Json` then rejects, which is how a valid-looking row turns into a 400 with the date
 * echoed back untouched.
 *
 * Slash- and dash-separated values are therefore read **day first**, matching
 * {@link DEFAULT_DATE_FORMAT} and the sites this runs at. Anything that isn't one of the recognised
 * shapes, or that isn't a real calendar date, returns null rather than a guess.
 */
export function parseImportDate(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  if (trimmed === '') return null;

  // Already ISO: yyyy-MM-dd, optionally with a time after a space or a T.
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(trimmed);
  if (iso) {
    const [, year, month, day, hour, minute, second] = iso;
    return buildIsoDate(Number(year), Number(month), Number(day), hour, minute, second);
  }

  // Day-first: d/M/yyyy or d-M-yyyy, optionally with a time.
  const dayFirst = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(trimmed);
  if (dayFirst) {
    const [, day, month, year, hour, minute, second] = dayFirst;
    return buildIsoDate(Number(year), Number(month), Number(day), hour, minute, second);
  }

  return null;
}

function buildIsoDate(year: number, month: number, day: number, hour?: string, minute?: string, second?: string): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Rejects the likes of 31/02 — the Date rolls over to March, which would silently shift the row.
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;

  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${year}-${pad(month)}-${pad(day)}`;

  if (hour === undefined || minute === undefined) return date;

  const hours = Number(hour);
  const minutes = Number(minute);
  const seconds = second === undefined ? 0 : Number(second);
  if (hours > 23 || minutes > 59 || seconds > 59) return date;

  // No zone suffix: an import date is a calendar date, not an instant.
  return `${date}T${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}
